import React from 'react'
import { Box, ButtonBase, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import palette from '../../../../theme/palette'
import { tableHeadSx } from '../../../common/sharedStyles'
import { BUCKET_LABEL, OWNER_KEYS, OWNERS } from '../constants'

/**
 * Bucket × owner counts for the active tab. Clicking a non-zero cell sets the bucket + owner filters
 * (the "All" row clears the bucket, the Total column clears the owner). The selected cell is highlighted.
 */
export default function AgeingMatrix({ tab, matrix, loading, selected, onPick }) {
  const undelivered = tab === 'U'
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{undelivered ? 'Ageing against attribution' : 'Lateness against attribution'}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
        {undelivered
          ? 'How long each undelivered breach has been sitting past EDD, against who owns it. This clock is still running. Click any number to filter the list.'
          : 'How late each delivered shipment landed against EDD, against who carries it in the record. This clock stopped at delivery. Click any number to filter the list.'}
      </Typography>
      {loading || !matrix ? <Skeleton variant='rectangular' height={190} /> : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadSx}>{undelivered ? 'Days past EDD' : 'Days late at delivery'}</TableCell>
              {OWNER_KEYS.map((o) => <TableCell key={o} align='right' sx={tableHeadSx}>{OWNERS[o].short}</TableCell>)}
              <TableCell align='right' sx={tableHeadSx}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.map((row) => {
              const bucket = row.bucket === 'All' ? '' : row.bucket
              const cells = [...OWNER_KEYS.map((o) => ({ o, n: row.counts?.[o] || 0 })), { o: '', n: row.total || 0 }]
              return (
                <TableRow key={row.bucket} sx={row.bucket === 'All' ? { '& td': { fontWeight: 700, borderTop: `2px solid ${palette.grey[300]}` } } : undefined}>
                  <TableCell sx={{ fontSize: 12 }}>{BUCKET_LABEL[row.bucket] || row.bucket}</TableCell>
                  {cells.map(({ o, n }) => {
                    const on = selected && selected.bucket === bucket && selected.owner === o && (bucket || o)
                    return (
                      <TableCell key={o || 'total'} align='right' sx={{ p: 0.5 }}>
                        <ButtonBase
                          disabled={!n}
                          onClick={() => onPick({ bucket, owner: o })}
                          aria-label={`${n} shipments, ${o ? OWNERS[o].short : 'all owners'}, ${BUCKET_LABEL[row.bucket]}`}
                          sx={{
                            minWidth: 40, px: 1, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: n ? 700 : 400,
                            fontVariantNumeric: 'tabular-nums',
                            color: n ? (o ? OWNERS[o].color : 'text.primary') : 'text.disabled',
                            bgcolor: on ? palette.primary.lighter : 'transparent',
                            outline: on ? `2px solid ${palette.primary.main}` : 'none',
                            '&:hover': { bgcolor: n ? palette.grey[200] : 'transparent' },
                          }}
                        >
                          {n}
                        </ButtonBase>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
